import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import AAAIrrigationDashboard from './Dashboard';
import { ServiceLocationProvider } from './ServiceLocationContext';
import { startPortalSession, currentPortalCustomerId } from './portalAuth';
import EstimateView from './EstimateView';
import InvoiceView from './InvoiceView';
import { smartBack } from './lib/navBack';
import WaterConservation from './WaterConservation';
import ZoneSchedule from './ZoneSchedule';
const Schedule = lazy(() => import('./Schedule'));
import Invoices from './Invoices';
import LetterView from './LetterView';
import LettersView from './LettersView';
import ImagesGallery from './ImagesGallery';
import Documents from './Documents';
import PaymentModal from './PaymentModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Login Page Component
function LoginPage() {
  // Single field — accepts either an email or a phone number. Not
  // advertised as dual-purpose in the UI copy (label/placeholder stay
  // email-flavored) per John's instruction: don't offer it up front, just
  // let the error message point someone to their phone if their email
  // doesn't match an account.
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const navigate = useNavigate();

  const looksLikeEmail = (v) => /\S+@\S+\.\S+/.test(v);
  const looksLikePhone = (v) => !v.includes('@') && v.replace(/\D/g, '').length >= 10;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    const value = identifier.trim();

    if (looksLikeEmail(value)) {
      try {
        const response = await axios.post(`${BACKEND_URL}/api/auth/request-magic-link`, {
          email: value
        });
        setMessage(response.data.message);
      } catch (err) {
        const detail = err.response?.data?.detail || 'Failed to send magic link';
        setError(
          err.response?.status === 404
            ? `${detail} If you'd rather use your phone number, enter it above instead.`
            : detail
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    if (looksLikePhone(value)) {
      try {
        await axios.post(`${BACKEND_URL}/api/auth/request-sms-code`, { phone: value });
        setPhone(value);
        setPhoneCodeSent(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to send code');
      } finally {
        setLoading(false);
      }
      return;
    }

    setError('Enter a valid email address or 10-digit phone number.');
    setLoading(false);
  };

  const resendCode = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/auth/request-sms-code`, { phone });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/verify-sms-code`, { phone, code: smsCode });
      startPortalSession(response.data.customer, response.data.session_token);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const loginBg = {
    background: `linear-gradient(135deg, rgba(13,79,140,0.82) 0%, rgba(27,122,191,0.78) 30%, rgba(8,145,178,0.78) 55%, rgba(5,150,105,0.75) 100%), url('${process.env.PUBLIC_URL}/logo2.png') center / cover no-repeat`,
    backgroundColor: '#0d4f8c'
  };

  return (
    <div className="login-page" style={loginBg}>
      {/* Watermark background — drop + text faded behind card */}
      <div className="login-watermark">
        <img src="/circle1.png" alt="" className="login-watermark-logo" />
        <div className="login-watermark-text">
          <span>AAA Irrigation Service</span>
          <span className="login-watermark-tagline">Integrity By The Drop</span>
          <span className="login-watermark-since">Since 2005</span>
        </div>
      </div>

      <div className="login-container">
        <div className="login-card">

          {/* Logo */}
          <div className="login-logo-wrap">
            <img src="/circle1.png" alt="AAA Irrigation Service" className="login-logo" />
          </div>

          <h1 className="login-title">AAA IRRIGATION SERVICE</h1>
          <p className="login-subtitle">CUSTOMER PORTAL</p>

          {message ? (
            <div className="success-message">
              <svg className="success-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h3>Check Your Email!</h3>
              <p>{message}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="login-info-box">
                <svg className="login-info-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="login-info-text">
                  Enter your email — we'll send a <strong>secure one-click login link</strong>. No password needed.
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="identifier" className="login-label">EMAIL ADDRESS</label>
                <div className="login-input-wrap">
                  <svg className="login-input-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <input
                    type="text"
                    id="identifier"
                    className="login-input"
                    placeholder="your@email.com"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              {error && <div className="login-error" data-testid="error-message">{error}</div>}

              <button
                type="submit"
                className="login-btn"
                disabled={loading}
                data-testid="login-button"
              >
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width:18,height:18}}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {loading ? 'SENDING...' : 'CONTINUE'}
              </button>
            </form>
          )}

          {!message && phoneCodeSent && (
            <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
              <p className="login-info-text" style={{ marginBottom: 12 }}>
                We texted a code to {phone}.
              </p>
              <form onSubmit={handleCodeSubmit}>
                <div className="form-group">
                  <label htmlFor="smsCode" className="login-label">6-DIGIT CODE</label>
                  <div className="login-input-wrap">
                    <input
                      type="text"
                      id="smsCode"
                      className="login-input"
                      placeholder="123456"
                      maxLength={6}
                      value={smsCode}
                      onChange={(e) => setSmsCode(e.target.value)}
                      required
                    />
                  </div>
                </div>
                {error && <div className="login-error">{error}</div>}
                <button type="submit" className="login-btn" disabled={loading}>
                  VERIFY CODE
                </button>
                <button
                  type="button"
                  className="login-btn"
                  style={{ marginTop: 8, background: 'transparent', border: '1px solid rgba(255,255,255,0.3)' }}
                  disabled={loading}
                  onClick={resendCode}
                >
                  RESEND CODE
                </button>
                <button
                  type="button"
                  className="login-btn"
                  style={{ marginTop: 8, background: 'transparent', border: '1px solid rgba(255,255,255,0.3)' }}
                  onClick={() => { setPhoneCodeSent(false); setPhone(''); setIdentifier(''); setError(''); }}
                >
                  USE A DIFFERENT NUMBER OR EMAIL
                </button>
              </form>
            </div>
          )}

          {/* Trust badges */}
          <div className="login-trust">
            <span className="login-trust-badge">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width:13,height:13}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              SECURE ACCESS
            </span>
            <span className="login-trust-badge">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width:13,height:13}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              ENCRYPTED LINK
            </span>
            <span className="login-trust-badge">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width:13,height:13}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              CUSTOMER TRUSTED
            </span>
          </div>

          {/* Premium footer */}
          <div className="login-footer">
            <div className="login-footer-divider" />
            <p>
              <span className="login-footer-powered">Powered by</span>
              <span className="login-footer-brand">Boatman Systems™</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Verify Token Page
function VerifyPage() {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      if (!token) {
        setError('No token provided');
        setLoading(false);
        return;
      }

      try {
        const estimateParam = urlParams.get('estimate');
        const invoiceParam = urlParams.get('invoice');
        let verifyUrl = `${BACKEND_URL}/api/auth/verify?token=${token}`;
        if (estimateParam) {
          verifyUrl += `&estimate=${estimateParam}`;
        } else if (invoiceParam) {
          verifyUrl += `&invoice=${invoiceParam}`;
        }
        const response = await axios.get(verifyUrl);
        startPortalSession(response.data.customer, response.data.session_token);
        setCustomer(response.data.customer);
        const redirectEstimate = response.data.redirect_estimate;
        if (redirectEstimate) {
          localStorage.setItem('open_estimate', redirectEstimate);
        }
        const redirectInvoice = response.data.redirect_invoice;
        if (redirectInvoice) {
          localStorage.setItem('open_invoice', redirectInvoice);
        }
        const tabParam = urlParams.get('tab');
        if (tabParam) {
          localStorage.setItem('open_tab', tabParam);
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired token');
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, []);

  if (loading) {
    return (
      <div className="loading-page">
        <div className="spinner"></div>
        <p>Verifying your access...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-page">
        <div className="error-card">
          <h2>Access Denied</h2>
          <p>{error}</p>
          <a href="/" className="btn-primary">Request New Link</a>
        </div>
      </div>
    );
  }

  if (customer) {
    const pendingEst = localStorage.getItem('open_estimate');
    if (pendingEst) {
      localStorage.removeItem('open_estimate');
      return <Navigate to={`/estimates?id=${pendingEst}&customer=${encodeURIComponent(customer.id)}`} replace />;
    }
    const pendingInv = localStorage.getItem('open_invoice');
    if (pendingInv) {
      localStorage.removeItem('open_invoice');
      return <Navigate to={`/invoice?id=${pendingInv}`} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return null;
}



// Quote Card with items, zone, approve/decline, and print
function QuoteCard({ quote, customerId }) {
  const isPending = ['Draft','Pending Approval','Sent for Approval'].includes(quote.custom_approval_status);
  const estimateUrl = `https://portal.aaairrigationservice.com/estimates/?mode=estimate&id=${quote.name}&customer=${encodeURIComponent(customerId)}`;

  return (
    <div className="quote-card card">
      <div className="quote-header">
        <h3>Estimate #{quote.name}</h3>
        <span className={`status-badge status-${(quote.custom_approval_status || quote.status || '').toLowerCase().replace(/ /g, '-')}`}>
          {quote.custom_approval_status || quote.status}
        </span>
      </div>
      <div className="quote-details">
        <p><strong>Date:</strong> {quote.transaction_date}</p>
        <p><strong>Total:</strong> ${parseFloat(quote.grand_total || 0).toFixed(2)}</p>
      </div>
      <div style={{ marginTop: 12 }}>
        <a
          href={estimateUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            textAlign: 'center',
            background: isPending ? '#ea580c' : '#1b7abf',
            color: '#fff',
            borderRadius: 8,
            padding: '12px 20px',
            fontWeight: 'bold',
            fontSize: 15,
            textDecoration: 'none',
          }}
        >
          {isPending ? '✍  View & Sign Estimate' : '📄  View Estimate'}
        </a>
      </div>
    </div>
  );
}

// Schedule Service Form Component
function ScheduleServiceForm({ customerId, customerEmail }) {
  const [formData, setFormData] = useState({
    service_type: 'Residential Repair',
    preferred_date: '',
    preferred_time: 'Any Time',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const fileInputRef = useRef(null);

  const handleImageSelect = (files) => {
    const newImages = Array.from(files).filter(f => f.type.startsWith('image/'));
    setSelectedImages([...selectedImages, ...newImages]);
  };

  const removeImage = (index) => {
    setSelectedImages(selectedImages.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      // First schedule the appointment
      const response = await axios.post(`${BACKEND_URL}/api/schedule-appointment`, {
        email: customerEmail,
        service: formData.service_type,
        preferred_date: formData.preferred_date,
        preferred_time: formData.preferred_time,
        notes: formData.description,
      });

      // Then upload any images
      if (selectedImages.length > 0) {
        for (let image of selectedImages) {
          const imgFormData = new FormData();
          imgFormData.append('image_file', image);
          imgFormData.append('customer_id', customerId);

          try {
            await axios.post(
              `${BACKEND_URL}/api/customer/${customerId}/upload-image`,
              imgFormData
            );
          } catch (imgErr) {
            console.warn('Image upload failed:', imgErr);
          }
        }
      }

      setMessage('✅ Appointment scheduled successfully! We will contact you soon.');
      setFormData({
        service_type: 'Residential Repair',
        preferred_date: '',
        preferred_time: 'Any Time',
        description: ''
      });
      setSelectedImages([]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to schedule appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="schedule-form card">
      <h2>Schedule a Service</h2>

      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="label">Service Type</label>
          <select
            className="input-field"
            value={formData.service_type}
            onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
            data-testid="service-type-select"
          >
            <option>Residential Repair</option>
            <option>Commercial Management</option>
            <option>Backflow Testing</option>
            <option>New Installation</option>
            <option>System Maintenance</option>
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="label">Preferred Date</label>
            <input
              type="date"
              className="input-field"
              value={formData.preferred_date}
              onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
              required
              data-testid="preferred-date-input"
            />
          </div>

          <div className="form-group">
            <label className="label">Preferred Time</label>
            <select
              className="input-field"
              value={formData.preferred_time}
              onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
              data-testid="preferred-time-select"
            >
              <option>Any Time</option>
              <option>8AM-11AM</option>
              <option>11AM-2PM</option>
              <option>2PM-5PM</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="label">Service Description</label>
          <textarea
            className="input-field"
            rows="4"
            placeholder="Describe the issue or service needed..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            data-testid="description-textarea"
          ></textarea>
        </div>

        <div className="form-group">
          <label className="label">📸 Problem Photos (Optional)</label>
          <div
            style={{
              border: '2px dashed #1b7abf',
              borderRadius: '4px',
              padding: '20px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: '#f9fafb',
              transition: 'all 0.2s'
            }}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.style.backgroundColor = '#eff6ff';
            }}
            onDragLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.backgroundColor = '#f9fafb';
              handleImageSelect(e.dataTransfer.files);
            }}
          >
            <p style={{ margin: 0, color: '#1b7abf', fontWeight: 'bold' }}>
              📸 Click or drag photos of the problem
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
              JPG or PNG
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png"
            onChange={(e) => handleImageSelect(e.target.files)}
            style={{ display: 'none' }}
          />

          {selectedImages.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#666' }}>
                Selected images ({selectedImages.length}):
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '8px' }}>
                {selectedImages.map((img, i) => (
                  <div
                    key={i}
                    style={{
                      position: 'relative',
                      paddingBottom: '100%',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}
                  >
                    <img
                      src={URL.createObjectURL(img)}
                      alt={`preview-${i}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        padding: 0,
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          type="submit"
          className="btn-primary full-width"
          disabled={loading}
          data-testid="schedule-button"
        >
          {loading ? 'Scheduling...' : 'Schedule Appointment'}
        </button>
      </form>
    </div>
  );
}


// Letters List Component
function LettersList({ customerId }) {
  const [letters, setLetters] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchLetters = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/customer/${customerId}/letters`
        );
        setLetters(response.data.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchLetters();
    }
  }, [customerId]);

  if (loading) return <div className="spinner"></div>;
  if (error) return <div style={{ color: '#dc2626' }}>❌ {error}</div>;

  if (letters.length === 0) {
    return (
      <div style={{
        backgroundColor: '#f0fdf4',
        border: '1px solid #dcfce7',
        borderRadius: '8px',
        padding: '30px',
        textAlign: 'center',
        color: '#166534'
      }}>
        <p style={{ fontSize: '16px', margin: 0 }}>✓ No letters at this time</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: '16px' }}>
      {letters.map((letter) => (
        <div
          key={letter.name}
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 5px 0', color: '#1a202c', fontSize: '16px' }}>
              {letter.title}
            </h3>
            <div style={{ color: '#666', fontSize: '14px' }}>
              {letter.letter_date && (
                <p style={{ margin: '3px 0' }}>
                  📅 {new Date(letter.letter_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
              {letter.recipient_name && (
                <p style={{ margin: '3px 0' }}>
                  👤 {letter.recipient_name}
                </p>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginLeft: '20px' }}>
            <button
              onClick={() => window.location.href = `/letter/${customerId}/${letter.name}`}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1b7abf',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              View
            </button>
            <button
              onClick={async () => {
                try {
                  const response = await axios.get(
                    `${BACKEND_URL}/api/customer/${customerId}/letter/${letter.name}/pdf`,
                    { responseType: 'blob' }
                  );
                  const url = window.URL.createObjectURL(response.data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${letter.name}.pdf`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  alert('Error downloading PDF: ' + err.message);
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#ea580c',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              📥 PDF
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// Action Result Page — shown after one-click approve/decline from email
function ActionResultPage() {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('status');
  const estimate = params.get('estimate');

  const messages = {
    approved: { title: 'Estimate Approved!', body: `Thank you! Estimate ${estimate} has been approved. We'll contact you shortly to confirm your appointment.`, color: '#059669' },
    declined: { title: 'Estimate Declined', body: `Estimate ${estimate} has been declined. Please contact us if you have any questions.`, color: '#6b7280' },
    already_processed: { title: 'Already Processed', body: 'This estimate has already been approved or declined.', color: '#ea580c' },
    expired: { title: 'Link Expired', body: 'This approval link has expired. Please contact us at (469) 751-3567.', color: '#dc2626' },
    invalid: { title: 'Invalid Link', body: 'This link is not valid. Please contact us at (469) 751-3567.', color: '#dc2626' },
    error: { title: 'Something Went Wrong', body: 'Please contact us at (469) 751-3567 to approve or decline your estimate.', color: '#dc2626' },
  };

  const msg = messages[status] || messages['error'];

  return (
    <div style={{ minHeight: '100vh', background: '#f4f7f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '40px 48px', maxWidth: 480, width: '90%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{status === 'approved' ? '✅' : status === 'declined' ? '👍' : '⚠️'}</div>
        <h2 style={{ color: msg.color, marginBottom: 12 }}>{msg.title}</h2>
        <p style={{ color: '#555', lineHeight: 1.6, marginBottom: 24 }}>{msg.body}</p>
        <div style={{ borderTop: '1px solid #eee', paddingTop: 20, color: '#aaa', fontSize: 12 }}>
          AAA Irrigation Service · (469) 751-3567<br />
          <span style={{ color: '#1b7abf', fontWeight: 'bold' }}>Powered by Boatman Systems™</span>
        </div>
      </div>
    </div>
  );
}

function EstimatesPage() {
  const params = new URLSearchParams(window.location.search);
  const quotationId = params.get('id');
  const customerId = params.get('customer');
  return (
    <EstimateView
      customerId={customerId}
      quotationId={quotationId}
      onClose={() => smartBack(customerId ? `/dashboard?customer=${encodeURIComponent(customerId)}` : '/dashboard')}
      onActioned={() => {}}
    />
  );
}

// The dashboard's property context must belong to the customer who is logged
// in NOW. Reading storage here (at route mount, after login wrote it) instead of
// once in App() is what makes a login inside the SPA take effect; `key` gives a
// different customer a brand-new provider, so no property state carries over.
function DashboardRoute() {
  const customerId = currentPortalCustomerId();
  return (
    <ServiceLocationProvider key={customerId || 'anonymous'} customerId={customerId}>
      <AAAIrrigationDashboard />
    </ServiceLocationProvider>
  );
}

// Main App Component
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/auth/verify" element={<VerifyPage />} />
        <Route path="/dashboard" element={<DashboardRoute />} />
        <Route path="/water-conservation" element={<WaterConservation />} />
        <Route path="/zone-schedule" element={<ZoneSchedule />} />
        <Route path="/schedule" element={<Suspense fallback={<div className="spinner" />}><Schedule /></Suspense>} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/letters" element={<LettersView />} />
        <Route path="/letter/:customerId/:letterId" element={<LetterView />} />
        <Route path="/action-result" element={<ActionResultPage />} />
        <Route path="/estimates" element={<EstimatesPage />} />
        <Route path="/invoice" element={<InvoiceView />} />
        <Route path="/invoice/:invoiceId" element={<InvoiceView />} />
      </Routes>
    </Router>
  );
}

export default App;